import {
  Grid,
  MobileStepper,
  Step,
  StepButton,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import React, { useState } from 'react'

import { rspTheme } from '../../styles/rspTheme'
import { TTrainStopSlide } from '../TrainstopSection'
import {
  NextPrevWrapper,
  RoundButton,
  StyledStepper,
  useStyles,
} from './styles'

interface TrainStopProps {
  activeSlide: number
  setActiveSlide: React.Dispatch<React.SetStateAction<number>>
  isTech?: boolean
  slides: readonly TTrainStopSlide[]
  theme: 'Dark' | 'Light'
}

const TrainStop = ({
  slides,
  activeSlide,
  setActiveSlide,
  theme = 'Light',
  isTech = false,
}: TrainStopProps) => {
  const classes = useStyles()
  const isMobile = useMediaQuery(`(max-width: 768px)`)
  const [activeSlideGroup, setActiveSlideGroup] = useState(0)
  // "slidesInGroup" represents how many trainstops have labels and wider spacing
  const slidesInGroup = 3
  // "maxNumber..." represents how many slides are passed to this module before the groups apply;
  // below this number of slides, all trainstop buttons have labels and wider spacing and no group
  // navigation buttons are shown
  const maxNumberOfLabelledSlides = 6

  // if there are more slides total than "maxNumberOfLabelledSlides",
  // we chunk the slides array into groups of "slidesInGroup"
  const slideGroups =
    slides.length > maxNumberOfLabelledSlides
      ? slides.reduce((resultArray: TTrainStopSlide[][], item, index) => {
          const chunkIndex = Math.floor(index / slidesInGroup)
          if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = []
          }
          resultArray[chunkIndex].push(item)
          return resultArray
        }, [])
      : [[...slides]]

  const handleSlide = (slide: number) => {
    setActiveSlide(slide)
    if (slides.length > maxNumberOfLabelledSlides) {
      setActiveSlideGroup(Math.floor(slide / slidesInGroup))
    }
  }

  const handleNextGroup = () => {
    if (activeSlide % slidesInGroup === slidesInGroup - 1) {
      if (activeSlideGroup < slideGroups.length) {
        setActiveSlideGroup(activeSlideGroup + 1)
        setActiveSlide((activeSlideGroup + 1) * slidesInGroup)
      }
    } else {
      handleNextSlide()
    }
  }

  const handlePrevGroup = () => {
    if (activeSlide % slidesInGroup === 0) {
      if (activeSlideGroup > 0) {
        setActiveSlideGroup(activeSlideGroup - 1)
        setActiveSlide(
          (activeSlideGroup - 1) * slidesInGroup + slidesInGroup - 1,
        )
      }
    } else {
      handlePrevSlide()
    }
  }

  const handleNextSlide = () => {
    if (activeSlide < slides.length) {
      setActiveSlide(activeSlide + 1)
    }
  }

  const handlePrevSlide = () => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1)
    }
  }

  return (
    <div
      style={{
        // @ts-ignore
        '--theme-color':
          theme === 'Light' ? rspTheme.palette.primary.main : '#ffffff',
      }}
    >
      {isMobile ? (
        <MobileStepper
          activeStep={activeSlide}
          backButton={
            <RoundButton
              className={`${theme.toLowerCase()} ${
                isTech && 'dark prevSlideButton'
              }`}
              disabled={activeSlide === 0}
              onClick={handlePrevSlide}
            >
              <>
                <NavigateBeforeIcon />
                <span className="sr-only">Previous Slide</span>
              </>
            </RoundButton>
          }
          className={classes.mobileRoot}
          nextButton={
            <RoundButton
              className={`${theme.toLowerCase()} ${
                isTech && 'dark nextSlideButton'
              }`}
              disabled={activeSlide === slides.length - 1}
              onClick={handleNextSlide}
            >
              <>
                <NavigateNextIcon />
                <span className="sr-only">Next Slide</span>
              </>
            </RoundButton>
          }
          position="static"
          steps={slides.length}
          variant="dots"
        />
      ) : (
        <Grid alignItems="flex-start" container justifyContent="space-between">
          {slides.length > maxNumberOfLabelledSlides && (
            <Grid
              alignItems="center"
              container
              item
              justifyContent="space-between"
              style={{ width: '100px', margin: '24px 48px 0 0' }}
            >
              <RoundButton
                className={theme.toLowerCase()}
                disabled={activeSlide === 0}
                onClick={handlePrevGroup}
              >
                <>
                  <NavigateBeforeIcon />
                  <span className="sr-only">Previous Slide Group</span>
                </>
              </RoundButton>
              <RoundButton
                className={theme.toLowerCase()}
                disabled={activeSlide === slides.length - 1}
                onClick={handleNextGroup}
              >
                <>
                  <NavigateNextIcon />
                  <span className="sr-only">Next Slide Group</span>
                </>
              </RoundButton>
            </Grid>
          )}
          <StyledStepper
            activeStep={activeSlide}
            className={`${classes.root} ${isTech ? 'technology' : ''} ${
              activeSlide === 0 ? 'firstActive' : ''
            }`}
            nonLinear
          >
            {isTech ? (
              <NextPrevWrapper>
                <RoundButton
                  className={`${theme.toLowerCase()} prevSlideButton`}
                  disabled={activeSlide === 0}
                  onClick={handlePrevSlide}
                >
                  <>
                    <NavigateBeforeIcon />
                    <span className="sr-only">Previous Slide</span>
                  </>
                </RoundButton>
                <RoundButton
                  className={`${theme.toLowerCase()} nextSlideButton`}
                  disabled={activeSlide === slides.length - 1}
                  onClick={handleNextSlide}
                >
                  <>
                    <NavigateNextIcon />
                    <span className="sr-only">Next Slide</span>
                  </>
                </RoundButton>
              </NextPrevWrapper>
            ) : null}
            {slideGroups.map((slideGroup, groupIndex: number) =>
              slideGroup.map((slide, index: number) => (
                <Step
                  className={
                    groupIndex === activeSlideGroup
                      ? 'activeGroup'
                      : 'inactiveGroup'
                  }
                  key={slide.id}
                >
                  <StepButton
                    disableTouchRipple
                    onClick={() =>
                      handleSlide(index + groupIndex * slidesInGroup)
                    }
                  >
                    <Typography
                      className={
                        groupIndex === activeSlideGroup ? 'visible' : 'hidden'
                      }
                      color="inherit"
                      component="span"
                      style={{ textTransform: 'uppercase', fontSize: '1.4rem' }}
                      variant="caption"
                    >
                      <span className="sr-only">Navigate to </span>
                      {slide.nav_label}
                    </Typography>
                  </StepButton>
                </Step>
              )),
            )}
            {!isTech && (
              <Step className="terminusContainer">
                <StepButton
                  className={classes.terminus}
                  onClick={() => {}}
                  tabIndex="-1"
                />
              </Step>
            )}
          </StyledStepper>
        </Grid>
      )}
    </div>
  )
}

export default TrainStop
